import { useMemo } from "react";
import { Column, ColumnDef } from "@tanstack/react-table";
import { StipendExpense } from "@common/utils/graphql";
import { DataTableColumnHeader } from "@common/components/Table/DataTableColumnHeader";
import { FilePreview } from "@/components/ui/file-preview";
import { AdminStipendsSelectedTab } from "./useAdminStipendsTabs";
import { formatDollars, formatDate } from "@common/components/utils";
import StatusCell from "./components/StatusCell";
import { Body } from "@hypercard/web-components";
import { PaymentMethod } from "../components/PaymentMethod";
import { StatusText } from "@common/components/StatusText";

export const useStipendsAdminColumns = (tab: AdminStipendsSelectedTab) => {
  const columns = useMemo(() => {
    return [
      {
        accessorKey: "stipendName",
        header: ({ column }) => {
          return <DataTableColumnHeader column={column} title="Stipend Name" />;
        },
      },
      {
        accessorKey: "employeeName",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="Employee" />
        ),
        cell: ({ row }) => {
          return (
            <Body size="md" weight="regular">
              {row.original.user.firstName} {row.original.user.lastName}
            </Body>
          );
        },
      },
      {
        accessorKey: "merchantName",
        header: ({ column }) => {
          return <DataTableColumnHeader column={column} title="Merchant" />;
        },
      },
      {
        accessorKey: "transactionDate",
        header: ({ column }) => {
          return <DataTableColumnHeader column={column} title="Date" />;
        },
        cell: ({ row }) => (
          <Body size="md" weight="regular">
            {formatDate(row.original.transactionDate)}
          </Body>
        ),
      },
      {
        accessorKey: "memo",
        header: ({ column }) => {
          return <DataTableColumnHeader column={column} title="Memo" />;
        },
        cell: ({ row }) => (
          <Body size="md" weight="regular">
            {row.original.notes || "No notes"}
          </Body>
        ),
      },
      {
        accessorKey: "receipt",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="Receipt" />
        ),
        cell: ({ row }) => {
          return <FilePreview url={row.original.receiptFileUrl} />;
        },
      },
      {
        accessorKey: "amount",
        header: ({ column }) => {
          return (
            <DataTableColumnHeader
              className="flex justify-end"
              column={column}
              title="Amount"
            />
          );
        },
        cell: ({ row }: any) => {
          return (
            <Body size="md" weight="regular" className="text-right">
              {formatDollars(row.original.amount)}
            </Body>
          );
        },
      },
      ...(tab === AdminStipendsSelectedTab.Pending
        ? [
            {
              id: "status",
              header: ({
                column,
              }: {
                column: Column<StipendExpense, unknown>;
              }) => (
                <DataTableColumnHeader
                  className="justify-end"
                  column={column}
                  title="Status"
                />
              ),
              accessorKey: "status",
              cell: ({ row }: any) => <StatusCell row={row} />,
            },
          ]
        : ([
            {
              id: "status",
              header: ({
                column,
              }: {
                column: Column<StipendExpense, unknown>;
              }) => (
                <DataTableColumnHeader
                  className="justify-end"
                  column={column}
                  title="Status"
                />
              ),
              cell: ({ row }) => {
                return (
                  <div className="text-right">
                    <StatusText {...row.original} />
                    <PaymentMethod stipendExpense={row.original} />
                  </div>
                );
              },
            },
          ] as ColumnDef<StipendExpense>[])),
    ] as ColumnDef<StipendExpense>[];
  }, [tab]);

  return {
    columns,
  };
};
